import { isValidGuid } from "../../../common/Utils";
import {
    CURRENT_TAB_COLORS_ON,
    DISPATCH_COLOR_CODES,
    DISPATCH_COLOR_DEFAULT,
    ROW_BAND,
    TAB_COLOR_BASIS,
    TOP_TABS,
    TRIP_STATUS_PLANNED,
} from "../constants";

/**
 * Row colouring for the dispatch board.
 *
 * The board has **two** rules, not one, and which tab you are on decides which applies.
 *
 * **Pick and Del - a date threshold.** From grdTopDispatchCustomDraw (uFMD0000.pas:7442) and
 * grdTopDelDispatchCustomDraw (uFMD0000.pas:9547):
 *
 *   if warningLevel <> 0 and scheduledDate < now + warningLevel hours  -> warning
 *   else if pendingLevel <> 0 and scheduledDate < now + pendingLevel hours -> pending
 *   else no colour
 *
 * Three details in there are easy to get wrong and are worth stating:
 *
 *  - Warning wins. The pending test is in the else branch of the warning test, so a row inside both
 *    bands is a warning row, never a pending one.
 *  - A level of 0 disables that band on its own, without disabling the other. The `<> 0` guard is
 *    per-band in the Delphi, not a single switch.
 *  - The level is in **hours**: the Delphi compares against `Now + (level / 24)`, and a TDateTime is
 *    counted in days.
 *  - The whole thing is gated on a profile being selected (`DispatchingProfile <> ''`). With no
 *    profile the board renders uncoloured, which is why every function here treats a missing profile
 *    as "no band" rather than as an error.
 *
 * **Current - trip assignment and trip status.** From grdTopCurrentDispatchCustomDraw
 * (uFMD0000.pas:9694). It reads no date and no threshold: a row not yet on a trip is left alone, a
 * planned trip is pending, and a trip that is already moving is a warning. Gated on
 * CURRENTTABCOLORS instead of on the profile levels.
 */

/** True when a level actually enables its band. Null, undefined and 0 all disable it. */
const bandEnabled = (level) => typeof level === "number" && level !== 0;

/**
 * Which band a row falls in, or ROW_BAND.NONE.
 *
 * @param {object} row     the grid row
 * @param {object} profile the selected Dispatching Data Profile, or null
 * @param {string} topTab  one of TOP_TABS
 * @param {Date}   now     injected so the thresholds are testable against a fixed clock
 */
export const getRowBand = (row, profile, topTab, now = new Date()) => {
    if (!row || !profile) {
        return ROW_BAND.NONE;
    }

    if (topTab === TOP_TABS.CURRENT) {
        // FMDISPATCHINGDP.CURRENTTABCOLORS - the Current tab alone can have colouring switched off
        // while the profile still supplies thresholds for the other tabs.
        if (profile.CurrentTabColors !== CURRENT_TAB_COLORS_ON) {
            return ROW_BAND.NONE;
        }

        // Not a date band. A dispatch object with no trip yet keeps the window default; once it is on
        // a trip, a planned trip is pending and anything further along is a warning.
        if (!isValidGuid(row.TripHeaderRowId)) {
            return ROW_BAND.NONE;
        }

        return row.TripStatus === TRIP_STATUS_PLANNED ? ROW_BAND.PENDING : ROW_BAND.WARNING;
    }

    const basis = TAB_COLOR_BASIS[topTab];
    if (!basis) {
        return ROW_BAND.NONE;
    }

    const scheduled = row[basis.dateField];
    if (!scheduled) {
        // A null scheduled date is outside both bands. The Delphi reads it as 0 (Delphi's zero date,
        // 1899) which would be "before now" and so always a warning; that is a quirk of AsDateTime on
        // a null field, not intent, and it would paint every undated row red. Treated as no band.
        return ROW_BAND.NONE;
    }

    const scheduledMs = new Date(scheduled).getTime();
    if (Number.isNaN(scheduledMs)) {
        return ROW_BAND.NONE;
    }

    const hoursToMs = (hours) => hours * 60 * 60 * 1000;
    const nowMs = now.getTime();

    const warningLevel = profile[basis.warningLevelField];
    if (bandEnabled(warningLevel) && scheduledMs < nowMs + hoursToMs(warningLevel)) {
        return ROW_BAND.WARNING;
    }

    const pendingLevel = profile[basis.pendingLevelField];
    if (bandEnabled(pendingLevel) && scheduledMs < nowMs + hoursToMs(pendingLevel)) {
        return ROW_BAND.PENDING;
    }

    return ROW_BAND.NONE;
};

/** Maps a Delphi colour code to its CSS class, or null for 'Z' / unmapped / missing. */
const colorClass = (code, kind) => {
    if (!code || code === DISPATCH_COLOR_DEFAULT) {
        return null;
    }

    const name = DISPATCH_COLOR_CODES[code];
    return name ? `dm-${kind}-${name}` : null;
};

/**
 * The CSS classes a row's cells should carry, given its band.
 *
 * Classes rather than inline styles, so the palette lives in dispatchManagement.css and a theme can
 * override it (the ticket asks for onCellPrepared / cellTemplate classes specifically).
 */
export const getRowClasses = (band, profile) => {
    if (!profile || band === ROW_BAND.NONE) {
        return [];
    }

    const background =
        band === ROW_BAND.WARNING ? profile.WarningColor : profile.PendingColor;
    const foreground =
        band === ROW_BAND.WARNING ? profile.WarningForegroundColor : profile.PendingForegroundColor;

    return [colorClass(background, "bg"), colorClass(foreground, "fg")].filter(Boolean);
};

/**
 * The classes a selected row's cells carry, from FMDISPATCHINGDP.SELECTBARCOLOR.
 *
 * The Delphi checks ASelected first and paints the whole row with SelectBarColor, foreground
 * clWindow, before it considers any band at all - the same three lines at the head of all four
 * CustomDraw handlers (uFMD0000.pas:7461, 7516, 9565, 9614, 9663, 9711). So selection wins over
 * warning and pending, and a profile whose SELECTBARCOLOR is 'Z' selects with no colour of its own,
 * which is the window default.
 *
 * The foreground is not taken from the profile: the Delphi hardcodes `AFont.Color := clWindow`,
 * which is the window background colour - white on a default scheme. dm-fg-white is the class for
 * that, so the selected row reads as reversed video the way the desktop's does.
 */
const getSelectedRowClasses = (profile) => {
    if (!profile) {
        return [];
    }

    const background = colorClass(profile.SelectBarColor, "bg");

    // No background means SELECTBARCOLOR is 'Z' or unmapped - leave the row entirely alone rather
    // than forcing a white foreground onto the grid's own selection styling.
    return background ? [background, "dm-fg-white"] : [];
};

/**
 * A DataGrid `onCellPrepared` handler that paints data rows for the given tab.
 *
 * Applied per cell rather than per row because DevExtreme exposes the cell element here; the band is
 * computed from the row, so every cell in a row gets the same classes.
 *
 * Selection is tested before the band, as the Delphi tests ASelected before anything else.
 * `e.isSelected` is DevExtreme's own flag for the checkbox selection the board drives Totals from;
 * the focused row is styled by the grid itself and is a different thing.
 */
export const buildOnCellPrepared = (profile, topTab) => (e) => {
    if (e.rowType !== "data" || !e.cellElement) {
        return;
    }

    const classes = e.isSelected
        ? getSelectedRowClasses(profile)
        : getRowClasses(getRowBand(e.data, profile, topTab), profile);

    classes.forEach((className) => e.cellElement.classList.add(className));
};
